import { useCallback, useRef } from 'react';
import type { PointerEvent } from 'react';

/**
 * Click/tap "splash" ripple effect. Pair the returned `onPointerDown` with the
 * `ripple-trigger` class (see studentDarkTheme.scss) on any element — including
 * the shared `<Button>` component via its className + rest-prop passthrough,
 * so this never requires editing shared component source.
 */
export function useRipple<T extends HTMLElement = HTMLElement>() {
    const cleanupTimers = useRef<Map<HTMLElement, number>>(new Map());

    const onPointerDown = useCallback((e: PointerEvent<T>) => {
        const el = e.currentTarget;
        const rect = el.getBoundingClientRect();
        el.style.setProperty('--ripple-x', `${e.clientX - rect.left}px`);
        el.style.setProperty('--ripple-y', `${e.clientY - rect.top}px`);

        // Restart the animation even on rapid repeat clicks.
        el.classList.remove('rippling');
        void el.offsetWidth; // force reflow
        el.classList.add('rippling');

        const existing = cleanupTimers.current.get(el);
        if (existing) window.clearTimeout(existing);
        const timer = window.setTimeout(() => {
            el.classList.remove('rippling');
            cleanupTimers.current.delete(el);
        }, 650);
        cleanupTimers.current.set(el, timer);
    }, []);

    return { onPointerDown };
}
